import { MouseEvent, ReactNode } from "react";
import { ArrowRight } from "@/components/UI/svgs";

export type SettingsNavKind = "i18n" | "kvp" | "org";

export const settingsRowId = (kind: SettingsNavKind, id: string | number) =>
    `settings-nav-${kind}-${id}`;

const TOP_5_PX = 20;

const scrollerOf = (el: HTMLElement): HTMLElement | null => {
    let node: HTMLElement | null = el.parentElement;
    while (node) {
        const overflowY = getComputedStyle(node).overflowY;
        if (overflowY === "auto" || overflowY === "scroll") return node;
        node = node.parentElement;
    }
    return null;
};

const stickyHeaderHeight = (el: HTMLElement, scroller: HTMLElement): number => {
    let node: HTMLElement | null = el;
    while (node && node !== scroller) {
        const prev = node.previousElementSibling as HTMLElement | null;
        if (prev && getComputedStyle(prev).position === "sticky") {
            return prev.getBoundingClientRect().height;
        }
        node = node.parentElement;
    }
    return 0;
};

const scrollRowToTop = (el: HTMLElement) => {
    const scroller = scrollerOf(el);
    if (!scroller) return;
    const offset = TOP_5_PX + stickyHeaderHeight(el, scroller);
    const delta =
        el.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top -
        offset;
    scroller.scrollTo({ top: scroller.scrollTop + delta, behavior: "smooth" });
};

/** Scroll a settings-tree row into view after expand/render has caught up. */
export const scrollSettingsRow = (kind: SettingsNavKind, id: string | number) => {
    const targetId = settingsRowId(kind, id);
    const tryScroll = (left: number) => {
        const el = document.getElementById(targetId);
        if (el) {
            scrollRowToTop(el);
            return;
        }
        if (left > 0) requestAnimationFrame(() => tryScroll(left - 1));
    };
    requestAnimationFrame(() => tryScroll(16));
}; 

/**
 * Grouped stack: a quiet caption, then one bordered card holding the rows. The
 * inspector is built out of these rather than out of nested list/entry pages.
 */
export const Section = ({
    title,
    hint,
    action,
    children,
}: {
    title: string;
    hint?: string;
    action?: ReactNode;
    children: ReactNode;
}) => (
    <section className="section">
        <div className="section-header">
            <div className="section-heading">
                <small className="caption">{title}</small>
                {hint && <small className="hint truncate">{hint}</small>}
            </div>
            {action}
        </div>
        <Card>{children}</Card>
    </section>
);

export const Card = ({ children }: { children: ReactNode }) => (
    <div className="card">{children}</div>
);

/**
 * A row inside a Card. Rows separate themselves so a Card never needs to know how
 * many children it has.
 */
export const Row = ({
    onClick,
    disabled,
    selected,
    leading,
    trailing,
    chevron,
    roomy,
    children,
}: {
    onClick?: () => void;
    disabled?: boolean;
    selected?: boolean;
    leading?: ReactNode;
    trailing?: ReactNode;
    chevron?: boolean;
    roomy?: boolean;
    children: ReactNode;
}) => (
    <div
        onClick={disabled ? undefined : onClick}
        className={[
            "row",
            roomy && "row-roomy",
            selected && "row-selected",
            onClick && !disabled && "row-interactive",
            disabled && "row-disabled",
        ].filter(Boolean).join(" ")}
    >
        {leading}
        <div className="flex-grow min-w-0">{children}</div>
        {trailing}
        {chevron && <span className="row-chevron"><ArrowRight size={16} /></span>}
    </div>
);

export const EmptyRow = ({ children }: { children: ReactNode }) => (
    <p className="row-empty">{children}</p>
);

export type PillTone = "neutral" | "accent" | "warn" | "danger" | "good";

const PILL_TONES: Record<PillTone, string> = {
    neutral: "",
    accent: "pill-accent",
    warn: "pill-warn",
    danger: "pill-danger",
    good: "pill-good",
};

export const Pill = ({
    tone = "neutral",
    title,
    onClick,
    children,
}: {
    tone?: PillTone;
    title?: string;
    onClick?: (event: MouseEvent) => void;
    children: ReactNode;
}) => (
    <span
        title={title}
        onClick={onClick}
        className={`pill ${PILL_TONES[tone]} ${onClick ? "pill-interactive" : ""}`}
    >
        {children}
    </span>
);

/** Label above a control, used for the small forms inside sections. */
export const Field = ({ label, children }: { label: string; children: ReactNode }) => (
    <label className="field">
        <small className="field-label">{label}</small>
        {children}
    </label>
);

export const TextField = ({
    value,
    onChange,
    placeholder,
    type = "text",
    readOnly,
    autoFocus,
}: {
    value: string;
    onChange: (val: string) => void;
    placeholder?: string;
    type?: string;
    readOnly?: boolean;
    autoFocus?: boolean;
}) => (
    <input
        type={type}
        value={value}
        readOnly={readOnly}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="input"
    />
);

export const SelectField = ({
    value,
    onChange,
    options,
    disabled,
}: {
    value: number | undefined;
    onChange: (val: number | undefined) => void;
    options: { id: number | undefined; name: string }[];
    disabled?: boolean;
}) => (
    <select
        disabled={disabled}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? undefined : parseInt(e.target.value))}
        className="select"
    >
        {options.map((option) => (
            <option key={option.id ?? "none"} value={option.id ?? ""}>
                {option.name}
            </option>
        ))}
    </select>
);

export const StringSelectField = ({
    value,
    onChange,
    options,
    disabled,
}: {
    value: string;
    onChange: (val: string) => void;
    options: { id: string; name: string }[];
    disabled?: boolean;
}) => (
    <select
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="select"
    >
        {options.map((option) => (
            <option key={option.id} value={option.id}>
                {option.name}
            </option>
        ))}
    </select>
);
